<script setup lang="ts">
import { computed, watch } from 'vue';
import MiniMapComponent from "@/components/Map/MiniMapComponent.vue";
import MiniMapPlaceholder from "@/components/MapColumn/MiniMapPlaceholder.vue";
import type {MiniMap, Coordinate} from '@/api/dx-backend/api';

const props = withDefaults(defineProps<{
  mapData?: MiniMap
  coordinates?: Coordinate
  mapError?: unknown
}>(), {
  mapData: () => ({}),
  mapError: null
});

const NO_ACCESS_STATUSES = [400, 403];

const errorStatus = computed<number | null>(() => {
  const error = props.mapError as { response?: { status?: number }, status?: number } | null;
  return error?.response?.status ?? error?.status ?? null;
});

const hasMap = computed(() => Boolean(props.mapData && (props.mapData as MiniMap).id));

const placeholderVariant = computed<'no-access' | 'offline'>(() => {
  const status = errorStatus.value;
  if (status === null || NO_ACCESS_STATUSES.includes(status)) {
    return 'no-access';
  }
  return 'offline';
});

watch(() => props.mapError, (error) => {
  if (!error) {
    return;
  }
  if (placeholderVariant.value === 'no-access') {
    console.debug('Mini map unavailable: no cartographic access for this character');
  } else {
    console.error('Mini map failed to load:', error);
  }
}, { immediate: true });
</script>

<template>
  <div class="map-column">
    <div class="map--disc">
      <MiniMapComponent v-if="hasMap" :map-data="mapData" width="100%" height="100%"></MiniMapComponent>
      <MiniMapPlaceholder v-else :variant="placeholderVariant"></MiniMapPlaceholder>
    </div>
    <div v-if="coordinates" class="map--coordinates">
      <span>x:{{ coordinates.x }}</span>
      <span>y:{{ coordinates.y }}</span>
      <span>z:{{ coordinates.z }}</span>
    </div>
  </div>
</template>

<style scoped>
.map-column {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  pointer-events: none;
}

.map--disc {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  overflow: visible;
}

.map--coordinates {
  display: flex;
  gap: 0.5rem;
  margin-top: -5%;
  padding: 0.25rem 0.7rem;
  border: 1.5px solid rgba(22, 234, 255, 0.9);
  border-radius: 999px;
  background-color: rgba(2, 10, 16, 0.85);
  font-size: 0.72rem;
  font-weight: bold;
  color: rgba(22, 234, 255, 0.95);
  box-shadow: 0 0 0.6rem rgba(22, 234, 255, 0.55),
              inset 0 0 0.6rem rgba(22, 234, 255, 0.28);
  text-shadow: 0 0 0.35rem rgba(22, 234, 255, 0.75);
}
</style>
